import { useCallback, useContext, useMemo, useState } from "react";
import type { Measurement } from "types";
import moment from "moment";
import Typography from "components/Typography";
import { useTranslation } from "react-i18next";
import Div from "components/primitives/Div";
import Button from "components/Button";
import type { DateTimeValidationError, PickerChangeHandlerContext } from "@mui/x-date-pickers-pro";
import DateTimePickers from "components/mui5/DateTimePickers";
import Dialog from "components/DialogV2";
import DialogHeader from "components/DialogV2/DialogHeader";
import DialogContent from "components/DialogV2/DialogContent";
import DialogClose from "components/DialogV2/DialogClose";
import DialogTitle from "components/DialogV2/DialogTitle";
import MeasurementsContext from "contexts/MeasurementsContext";
import useExercises from "providers/ExercisesProvider/useExercises";
import DialogActions from "components/DialogV2/DialogActions";

interface EditMeasurementDialogProps {
  measurement: Measurement;
  open: boolean;
  onClose: () => void;
}

function EditMeasurementDialog(props: EditMeasurementDialogProps) {
  const { measurement, onClose, open } = props;
  const [date, setDate] = useState(moment(measurement.completedDate));
  const { t } = useTranslation();
  const exercises = useExercises();

  const measurementContext = useContext(MeasurementsContext);
  const [, actions] = measurementContext || [];

  const exercise = useMemo(
    () => exercises.find((exercise) => exercise.id === measurement.exerciseId),
    [exercises, measurement.exerciseId]
  );

  const handleOnChange = useCallback(
    (value: moment.Moment | null, _: PickerChangeHandlerContext<DateTimeValidationError>) => {
      if (!value) return;
      setDate(value);
    },
    []
  );

  const handleOnSave = useCallback(() => {
    actions?.updateCompletedDate(measurement.id, date.toISOString());
    onClose();
  }, [actions, date, measurement.id, onClose]);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogHeader>
        <DialogTitle>{t("charting.edit_exercise_measurement")}</DialogTitle>
        <DialogClose />
      </DialogHeader>

      <DialogContent sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <Div sx={{ mb: 2, width: "100%" }}>
          <Typography fontWeight="fontWeightMedium" mb={2}>
            {exercise?.name}
          </Typography>
          <DateTimePickers.DateTimePicker
            label={t("charting.select_date_and_time")}
            format="DD/MM/YYYY HH:mm A"
            value={date}
            onChange={handleOnChange}
            sx={{ width: "100%" }}
          />
        </Div>
      </DialogContent>
      <DialogActions sx={{ gap: 2 }}>
        <Button variant="tertiary" onClick={onClose} fullWidth>
          {t("global.cancel_button_text")}
        </Button>
        <Button fullWidth onClick={handleOnSave}>
          {t("global.save_button_text")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditMeasurementDialog;
